import React, {useEffect} from 'react';
import {ChartsYAxis, LineChart, ResponsiveChartContainer, ScatterChart} from "@mui/x-charts";
import {
    Box, Divider,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Typography,
    useMediaQuery
} from "@mui/material";
import {useNavigation} from "react-router-dom";
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import {useTranslation} from "react-i18next";


export const Charts = ({filters, details, setCurrentFilter, currentFilter}) => {
    const nav = useNavigation();
    const X_DATA_KEY = "timestamp_date";
    const Y_DATA_KEY = "median";
    const [value, setValue] = React.useState('0');
    const { t } = useTranslation();
    const TAB_MARGIN_WIDTH = "200px";
    const isMobile = useMediaQuery('(max-width:768px)');

    useEffect(() => {
        if (!details || !filters) {
            nav.navigate("/");
        }
    });

    function calculateTickMinStep(dates) {
        if (dates.length < 2) return 24 * 60 * 60 * 1000;

        let maxDiff = dates[dates.length -1].timestamp_date - dates[0].timestamp_date;

        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;
        const month = 30 * day;
        const year = 365 * day;

        let tickMinStep = minute * 30;

        if (maxDiff > year) tickMinStep = year;
        else if (maxDiff > month) tickMinStep = month;
        else if (maxDiff > day) tickMinStep = day;
        else if (maxDiff > hour) tickMinStep = hour;

        return tickMinStep;
    }

    const getFilterName = (name) => {
        return t(`birdhouseFilters:${name}`);
    };

    const getSensorName = (name) => {
        return t(`sensors:${name}`);
    };

    const handleTabChange = (event, newValue) => {
        if(value === newValue) return;
        setValue(`${newValue}`);
    };

    const handleFilterChange = (event) => {
        setCurrentFilter(event.target.value);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    return <Box sx={{ paddingRight: 2, height: "100%" }} width="100vw">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabContext value={value} >
                <Stack direction="row"
                       alignItems="center"
                       justifyContent={isMobile ? 'center' : 'space-between'}
                       sx={{ borderBottom: 1, paddingX: 1, marginTop: -1, borderColor: 'divider' }}>
                    {!isMobile && <div style={{width: TAB_MARGIN_WIDTH}}/>}
                    <TabList centered value={value} onChange={handleTabChange} aria-label="Tabs">
                        {details.sensors.map((sensor, index) => (
                            <Tab sx={{ fontSize: isMobile ? '0.75rem' : 'auto' }} key={index} value={`${index}`} label={getSensorName(sensor.type)} />
                        ))}
                    </TabList>
                    {!isMobile && <FormControl>
                        <InputLabel id="filter-label">Filtr</InputLabel>
                        <Select
                            size="small"
                            labelId="filter-label"
                            value={currentFilter}
                            label="Filte"
                            onChange={handleFilterChange}
                            sx={{ width: TAB_MARGIN_WIDTH }}
                            style={{ height: 35 }}
                        >
                            {filters.map((element, index) => (<MenuItem key={index} value={element}>{getFilterName(element)}</MenuItem>))}
                        </Select>
                    </FormControl>}
                </Stack>
                {isMobile && <Stack spacing={1} width="100%" marginTop="10px">
                    <Stack width="100%" marginY="30" justifyContent="center" alignItems="center">
                        <FormControl>
                            <InputLabel id="filter-label">Filtr</InputLabel>
                            <Select
                                size="small"
                                labelId="filter-label"
                                value={currentFilter}
                                label="Filte"
                                onChange={handleFilterChange}
                                sx={{ width: TAB_MARGIN_WIDTH }}
                                style={{ height: 35 }}
                            >
                                {filters.map((element, index) => (<MenuItem key={index} value={element}>{getFilterName(element)}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Divider />
                </Stack>}
                {details.sensors.map((sensor, index) => (
                    <TabPanel key={sensor.type + index} value={`${index}`}>
                        <Grid2 key={index} size={{ xs: 12, md: 6 }} justifyContent="center" alignItems="center">
                            {!sensor.measurements_count ? (<Stack width="100%" alignItems="center" justifyContent="center" sx={{ py: '20vh' }}>
                                    <Typography variant="h5">Brak danych do wyświetlenia</Typography>
                                </Stack>) :
                                <Stack alignItems="center" justifyContent="center" sx={{ padding: 2 }}>
                                    <Stack alignItems="center" justifyContent="center" height={isMobile ? `${(window.innerWidth * 0.9) * (3 / 4)}px` : '60vh'}
                                           width={isMobile ? '90vw' : `${(window.innerWidth * 0.6) * (4 / 3)}px`}>
                                        <LineChart
                                            key={index + sensor.type}
                                            colors={['#2B4970']}
                                            grid={{ horizontal: true, vertical: true }}
                                            dataset={sensor.measurements}
                                            xAxis={[
                                                {
                                                    curve: "linear",
                                                    dataKey: X_DATA_KEY,
                                                    scaleType: 'time',
                                                    label: "Czas",
                                                    tickFormatter: (value) =>
                                                        new Date(value).toLocaleDateString('en-US', {
                                                            day: 'numeric', month: 'short'
                                                        }),
                                                    tickMinStep: calculateTickMinStep(sensor.measurements),
                                                }
                                            ]}
                                            series={[
                                                {
                                                    domain: [0, 32],
                                                    curve: "linear",
                                                    label: "Wartość",
                                                    dataKey: Y_DATA_KEY,
                                                    area: false,
                                                    showMark: (v) => {
                                                        const hasNewValue = sensor.measurements.length > v.index + 1 && sensor.measurements[v.index + 1].median;
                                                        const hasPastValue = v.index !== 0 && sensor.measurements[v.index - 1].median;
                                                        return !hasNewValue || !hasPastValue;
                                                    },
                                                    valueFormatter: (v, { dataIndex }) => {
                                                        return <span>
                                                        <span>{`Mediana: ${sensor.measurements[dataIndex].median || sensor.measurements[dataIndex].max}`}</span>
                                                        <br/>
                                                        <span>{`Min: ${sensor.measurements[dataIndex].min}`}</span>
                                                        <br/>
                                                        <span>{`Maks: ${sensor.measurements[dataIndex].max}`}</span>
                                                        <br/>
                                                        <span>{`Pierwszy pomiar: ${formatDate(sensor.measurements[dataIndex].first_timestamp)}`}</span>
                                                        <br/>
                                                        <span>{`Ostatni pomiar: ${formatDate(sensor.measurements[dataIndex].last_timestamp)}`}</span>
                                                    </span>;
                                                    },
                                                },
                                            ]}
                                        />
                                    </Stack>
                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                        <Typography>Mediana: {sensor.median}</Typography>
                                        <Typography>Min: {sensor.min}</Typography>
                                        <Typography>Maks: {sensor.max}</Typography>
                                    </Stack>
                                </Stack>}
                        </Grid2>
                    </TabPanel>
                ))}
            </TabContext>
        </Box>
    </Box>
}